// Sala del médico (Sala CRM↔HCE, rebanada 5; PLAN 4.11): la lista de SUS pacientes del día por hora de llegada, con las acciones Llamar / Volver a llamar /
// Iniciar consulta / Finalizar / No asistió. Es UN solo componente: lo usa la página «Sala» del menú y el bloque del Dashboard (debajo del calendario).
//
// Todo lo que importa lo decide el servidor: qué turnos son suyos, el tiempo de espera (su reloj), el tope de llamados, las transiciones válidas. Cada
// acción manda el estado que esta pantalla VIO; si cambió (otra pestaña, recepción), el servidor responde 409 y la Sala se vuelve a pedir sola.
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { notify } from '@/components/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  accionesDeFila,
  deshacerTurno,
  esperaActualSeg,
  esperaLegible,
  fijarConsultorioDelDia,
  finalizarTurno,
  horaEcuadorDe,
  iniciarTurno,
  llamarTurno,
  marcarAsistio,
  noAsistioTurno,
  type AccionFila,
  type CitaPorLlegar,
  type ErrorSala,
  type FilaSala,
  type ResultadoIniciar,
  type ResumenPendientes,
} from '@/lib/salaHce';
import { useSalaDelDia } from '@/lib/useSalaDelDia';
import { ahoraServidorMs, conectarYAbrir, finalizarVirtual, noAsistioVirtual, type AccionVirtual, type CitaVirtual } from '@/lib/telemedicinaHce';
import { useVirtualesDelDia } from '@/lib/useVirtualesDelDia';
import { EtiquetaVirtual } from './EtiquetaVirtual';
import { FilaVirtual } from './FilaVirtual';
import { IndicadorEnVivo } from './IndicadorEnVivo';

type Confirmacion =
  | { tipo: 'finalizar'; fila: FilaSala; resumen: ResumenPendientes }
  | { tipo: 'noAsistio'; fila: FilaSala }
  | { tipo: 'deshacer'; fila: FilaSala }
  | { tipo: 'asistio'; cita: CitaPorLlegar }
  | { tipo: 'finalizarVirtual'; cita: CitaVirtual; pendientes: number; claves: string[] }
  | { tipo: 'noAsistioVirtual'; cita: CitaVirtual };

const ES_ACTIVA = (f: FilaSala): boolean => f.estado === 'waiting' || f.estado === 'called' || f.estado === 'in_consult' || f.consultaAbierta;

export function SalaPanel({ variante = 'pagina' }: { variante?: 'pagina' | 'dashboard' }): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sala, cargando, errorCarga, sinSala, enVivo, ocupados, recibidoEnMs, ahoraMs, recargar, ejecutar } = useSalaDelDia();
  const [mensajeError, setMensajeError] = useState<string | null>(null);
  const [confirmacion, setConfirmacion] = useState<Confirmacion | null>(null);
  const [consultorioTexto, setConsultorioTexto] = useState('');
  const [guardandoConsultorio, setGuardandoConsultorio] = useState(false);
  // Citas de hoy a las que se les está marcando «Asistió» (un doble clic no repite la acción).
  const [asistiendo, setAsistiendo] = useState<ReadonlySet<string>>(new Set());
  // Citas virtuales (telemedicina): su propia lista y sus propias acciones. El enlace de la videollamada se muestra aquí si el navegador bloqueó la ventana.
  const virt = useVirtualesDelDia();
  const [enlaceSala, setEnlaceSala] = useState<{ citaId: string; link: string } | null>(null);
  const ahoraSrvMs = ahoraServidorMs(virt.ahoraIso, virt.recibidoEnMs, virt.ahoraMs);

  // La teleconsulta ya conectada también es un turno (sin número) en `/api/hce/sala`: se muestra UNA vez, como fila virtual (el turno es el `ticketId`).
  const ticketsVirtuales = new Set(virt.citas.map((c) => c.ticketId).filter((id): id is number => id !== null));
  const filas = (sala?.filas ?? []).filter((f) => !ticketsVirtuales.has(f.id));
  const activas = filas.filter(ES_ACTIVA);
  const cerradas = filas.filter((f) => !ES_ACTIVA(f));
  const soloLectura = sala?.soloLectura === true;
  const puedeConsultar = sala?.puedeConsultar !== false;
  const volverAlHabitual = consultorioTexto.trim() === '' && Boolean(sala?.consultorio?.delDia);

  const mostrarError = (e: ErrorSala): void => {
    setMensajeError(e.status === 409 && e.codigo === 'conflicto' ? t('sala.resultado.turnoCambio') : e.message);
  };

  function abrirHistoria(r: ResultadoIniciar): void {
    if (!r.medplumPatientId) {
      notify({ color: 'yellow', message: t('sala.resultado.sinPacienteHce'), autoClose: 8000 });
      return;
    }
    if (r.encounterPendiente) notify({ color: 'yellow', message: t('sala.resultado.historiaPendiente'), autoClose: 8000 });
    // Un solo paso: la ficha del paciente, en Notas (igual que «Iniciar nueva consulta» de la ficha).
    void navigate(`/${r.medplumPatientId}/notas`);
  }

  async function accionar(f: FilaSala, a: AccionFila): Promise<void> {
    setMensajeError(null);
    if (a === 'llamar' || a === 'volverALlamar') {
      const r = await ejecutar(f.id, () => llamarTurno(f.id, f.estado), mostrarError);
      if (r?.sinConsultorio) notify({ color: 'yellow', message: t('sala.resultado.llamadoSinConsultorio'), autoClose: 7000 });
    } else if (a === 'iniciar' || a === 'abrirHistoria' || a === 'reintentarHistoria') {
      const r = await ejecutar(f.id, () => iniciarTurno(f.id, f.estado), mostrarError);
      if (r) abrirHistoria(r);
    } else if (a === 'finalizar' || a === 'cerrarConsulta') {
      const r = await ejecutar(f.id, () => finalizarTurno(f.id, f.estado), mostrarError);
      if (!r) return;
      if ('requiereConfirmacion' in r) setConfirmacion({ tipo: 'finalizar', fila: f, resumen: r.resumen });
      else if (r.encounterCerrado === false) notify({ color: 'yellow', message: t('sala.resultado.consultaSinCerrar'), autoClose: 8000 });
    } else if (a === 'noAsistio') {
      setConfirmacion({ tipo: 'noAsistio', fila: f });
    } else if (a === 'deshacer') {
      setConfirmacion({ tipo: 'deshacer', fila: f });
    }
  }

  /** «Asistió» del médico: crea el turno de la cita (igual que recepción). El servidor decide si la cita es suya y de hoy; la Sala se vuelve a pedir sola.
   *  Se confirma antes (`confirmar`): marcar la llegada por error no siempre se puede deshacer. */
  async function marcarLlegada(c: CitaPorLlegar): Promise<void> {
    if (asistiendo.has(c.citaId)) return;
    setMensajeError(null);
    setAsistiendo((prev) => new Set(prev).add(c.citaId));
    try {
      const r = await marcarAsistio(c.citaId);
      notify({ color: 'green', message: r.turno ? t('sala.porLlegar.hechoConTurno', { nombre: c.nombre, turno: r.turno }) : t('sala.porLlegar.hecho', { nombre: c.nombre }), autoClose: 6000 });
    } catch (err) {
      setMensajeError(err instanceof Error && err.message ? err.message : t('sala.porLlegar.error'));
    } finally {
      setAsistiendo((prev) => {
        const siguiente = new Set(prev);
        siguiente.delete(c.citaId);
        return siguiente;
      });
      await recargar();
    }
  }

  async function accionarVirtual(c: CitaVirtual, a: AccionVirtual): Promise<void> {
    setMensajeError(null);
    if (a === 'conectar' || a === 'volverAEntrar') {
      // La ventana se abre en el clic (antes de esperar al servidor) para que el navegador no la bloquee; ver `conectarYAbrir`.
      setEnlaceSala(null);
      const r = await virt.ejecutar(c.citaId, () => conectarYAbrir(c.citaId, c.estado), mostrarError);
      if (r && !r.abierta) setEnlaceSala({ citaId: c.citaId, link: r.link });
      if (r && r.resultado.cambio && !r.resultado.encounterId) notify({ color: 'yellow', message: t('sala.virtual.historiaPendiente'), autoClose: 8000 });
    } else if (a === 'finalizar') {
      const r = await virt.ejecutar(c.citaId, () => finalizarVirtual(c.citaId, c.estado), mostrarError);
      if (!r) return;
      if (!r.ok) setConfirmacion({ tipo: 'finalizarVirtual', cita: c, pendientes: r.pendientes, claves: r.claves });
      else if (r.encounterCerrado !== true) notify({ color: 'yellow', message: t('sala.resultado.consultaSinCerrar'), autoClose: 8000 });
    } else {
      setConfirmacion({ tipo: 'noAsistioVirtual', cita: c });
    }
  }

  async function confirmar(): Promise<void> {
    const c = confirmacion;
    if (!c) return;
    setConfirmacion(null);
    if (c.tipo === 'asistio') {
      await marcarLlegada(c.cita);
    } else if (c.tipo === 'noAsistioVirtual') {
      await virt.ejecutar(c.cita.citaId, () => noAsistioVirtual(c.cita.citaId, c.cita.estado), mostrarError);
    } else if (c.tipo === 'finalizarVirtual') {
      const r = await virt.ejecutar(c.cita.citaId, () => finalizarVirtual(c.cita.citaId, c.cita.estado, true), mostrarError);
      if (r && r.ok && r.encounterCerrado !== true) notify({ color: 'yellow', message: t('sala.resultado.consultaSinCerrar'), autoClose: 8000 });
    } else if (c.tipo === 'noAsistio') {
      await ejecutar(c.fila.id, () => noAsistioTurno(c.fila.id, c.fila.estado), mostrarError);
    } else if (c.tipo === 'deshacer') {
      await ejecutar(c.fila.id, () => deshacerTurno(c.fila.id, c.fila.estado), mostrarError);
    } else {
      const r = await ejecutar(c.fila.id, () => finalizarTurno(c.fila.id, c.fila.estado, true), mostrarError);
      if (r && 'encounterCerrado' in r && r.encounterCerrado === false) notify({ color: 'yellow', message: t('sala.resultado.consultaSinCerrar'), autoClose: 8000 });
    }
  }

  async function guardarConsultorio(): Promise<void> {
    setGuardandoConsultorio(true);
    setMensajeError(null);
    try {
      const r = await fijarConsultorioDelDia(consultorioTexto);
      notify({ color: 'green', message: r.delDia ? t('sala.consultorio.guardado', { nombre: r.delDia }) : t('sala.consultorio.restablecido') });
      setConsultorioTexto('');
      await recargar();
    } catch (err) {
      setMensajeError(err instanceof Error && err.message ? err.message : t('sala.consultorio.error'));
    } finally {
      setGuardandoConsultorio(false);
    }
  }

  // Una cuenta sin Sala (403) no ve nada: ni la lista ni una alerta.
  // (Si la Sala presencial responde 403 pero hay citas virtuales, se muestran esas.)
  if (sinSala && virt.citas.length === 0) return <></>;

  const tituloId = variante === 'dashboard' ? 'sala-bloque-titulo' : 'sala-pagina-titulo';

  return (
    <section aria-labelledby={tituloId} className="space-y-3 rounded-lg border border-border bg-card p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id={tituloId} className="text-sm font-medium text-hce-ink">
          {t(variante === 'dashboard' ? 'sala.tituloBloque' : 'sala.titulo')}
          {sala && <span className="ml-2 font-mono text-xs text-hce-muted">{t('sala.esperando', { count: sala.esperando })}</span>}
        </h2>
        <IndicadorEnVivo estado={enVivo} />
      </div>

      {!soloLectura && sala && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-hce-muted" htmlFor={`consultorio-${variante}`}>
            {t('sala.consultorio.etiqueta')}
            <span className="flex gap-2">
              <Input
                id={`consultorio-${variante}`}
                className="h-8 w-52"
                maxLength={40}
                value={consultorioTexto}
                placeholder={sala.consultorio?.efectivo ?? t('sala.consultorio.placeholder')}
                onChange={(e) => setConsultorioTexto(e.target.value)}
              />
              <Button size="sm" variant="outline" disabled={guardandoConsultorio || (consultorioTexto.trim() === '' && !volverAlHabitual)} onClick={() => void guardarConsultorio()}>
                {volverAlHabitual ? t('sala.consultorio.volverAlHabitual') : t('sala.consultorio.guardar')}
              </Button>
            </span>
          </label>
          {sala.consultorio?.efectivo && <p className="pb-1 text-xs text-hce-muted">{sala.consultorio.delDia ? t('sala.consultorio.guardado', { nombre: sala.consultorio.efectivo }) : t('sala.consultorio.habitual', { nombre: sala.consultorio.efectivo })}</p>}
        </div>
      )}
      {!soloLectura && sala && !sala.consultorio?.efectivo && (
        <Alert variant="orange">
          <AlertDescription>{t('sala.consultorio.sin')}</AlertDescription>
        </Alert>
      )}

      {mensajeError && (
        <Alert variant="red">
          <AlertDescription>{mensajeError}</AlertDescription>
        </Alert>
      )}
      {enlaceSala && (
        <Alert variant="orange">
          <AlertDescription>
            {t('sala.virtual.ventanaBloqueada')}{' '}
            <a className="font-medium underline" href={enlaceSala.link} target="_blank" rel="noopener noreferrer" onClick={() => setEnlaceSala(null)}>
              {t('sala.virtual.abrirVideollamada')}
            </a>
          </AlertDescription>
        </Alert>
      )}
      {errorCarga && !sala && (
        <Alert variant="red">
          <AlertDescription>{t('sala.errorCargar')}</AlertDescription>
        </Alert>
      )}

      {virt.error && (
        <Alert variant="red">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>{t('sala.virtual.errorCargar')}</span>
            <Button size="sm" variant="outline" onClick={() => void virt.recargar()}>
              {t('sala.virtual.reintentar')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Cada lista se muestra por su cuenta: si una falla o tarda, la otra no se oculta. */}
      {cargando && !sala && virt.citas.length === 0 ? (
        <p className="text-sm text-hce-muted">{t('sala.cargando')}</p>
      ) : sala && filas.length === 0 && virt.citas.length === 0 ? (
        <p className="text-sm text-hce-muted">{t('sala.sinPacientes')}</p>
      ) : sala || virt.citas.length > 0 ? (
        <>
          {(activas.length > 0 || virt.citas.length > 0) && (
            <TablaFilas
              titulo={t('sala.enSala')}
              filas={activas}
              virtuales={virt.citas}
              ocupadasVirtuales={virt.ocupadas}
              ahoraSrvMs={ahoraSrvMs}
              onAccionVirtual={(c, a) => void accionarVirtual(c, a)}
              soloLectura={soloLectura}
              puedeConsultar={puedeConsultar}
              ocupados={ocupados}
              recibidoEnMs={recibidoEnMs}
              ahoraMs={ahoraMs}
              onAccion={(f, a) => void accionar(f, a)}
            />
          )}
          {cerradas.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs text-hce-muted">
                {t('sala.atendidosHoy')} ({cerradas.length})
              </summary>
              <TablaFilas
                titulo={t('sala.atendidosHoy')}
                filas={cerradas}
                soloLectura={soloLectura}
                puedeConsultar={puedeConsultar}
                ocupados={ocupados}
                recibidoEnMs={recibidoEnMs}
                ahoraMs={ahoraMs}
                onAccion={(f, a) => void accionar(f, a)}
              />
            </details>
          )}
        </>
      ) : null}

      {/* Citas de hoy de este médico que aún no llegaron: «Asistió» crea su turno (la misma operación que hace recepción). */}
      {!soloLectura && sala && (sala.citasPorLlegar?.length ?? 0) > 0 && (
        <div className="space-y-1.5">
          <h3 className="text-xs font-medium text-hce-muted">{t('sala.porLlegar.titulo')}</h3>
          <ul className="divide-y divide-border rounded-md border border-border">
            {(sala.citasPorLlegar ?? []).map((c) => (
              <li key={c.citaId} data-cita-por-llegar className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <span className="text-sm text-hce-ink">
                  <span className="font-mono text-xs text-hce-muted">{horaEcuadorDe(c.citaHora) || '—'}</span> <span className="font-medium">{c.nombre}</span>
                  {c.motivo && <span className="text-hce-muted"> · {c.motivo}</span>}
                </span>
                <Button size="sm" variant="outline" disabled={asistiendo.has(c.citaId)} aria-label={t('sala.accion.de', { accion: t('sala.porLlegar.asistio'), nombre: c.nombre })} onClick={() => setConfirmacion({ tipo: 'asistio', cita: c })}>
                  {t('sala.porLlegar.asistio')}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={confirmacion !== null} onOpenChange={(abierto) => !abierto && setConfirmacion(null)}>
        <DialogContent>
          {confirmacion?.tipo === 'finalizar' && (
            <>
              <DialogHeader>
                <DialogTitle>{t('sala.confirmar.pendientes', { count: confirmacion.resumen.total })}</DialogTitle>
                <DialogDescription>{confirmacion.fila.nombre}</DialogDescription>
              </DialogHeader>
              <ul className="list-disc space-y-1 pl-5 text-sm text-hce-ink">
                {confirmacion.resumen.formulariosEnProgreso > 0 && <li>{t('sala.confirmar.detalleEnProgreso', { count: confirmacion.resumen.formulariosEnProgreso })}</li>}
                {confirmacion.resumen.alertas.map((a) => (
                  <li key={a}>{t(`sala.confirmar.alerta_${a}`, a)}</li>
                ))}
              </ul>
            </>
          )}
          {confirmacion?.tipo === 'finalizarVirtual' && (
            <>
              <DialogHeader>
                <DialogTitle>{t('sala.confirmar.pendientes', { count: confirmacion.pendientes })}</DialogTitle>
                <DialogDescription>{confirmacion.cita.pacienteNombre}</DialogDescription>
              </DialogHeader>
              <ul className="list-disc space-y-1 pl-5 text-sm text-hce-ink">
                {confirmacion.claves.map((a) => (
                  <li key={a}>{t(`sala.confirmar.alerta_${a}`, a)}</li>
                ))}
              </ul>
            </>
          )}
          {confirmacion?.tipo === 'noAsistioVirtual' && (
            <DialogHeader>
              <DialogTitle>{t('sala.confirmar.noAsistioTitulo', { nombre: confirmacion.cita.pacienteNombre })}</DialogTitle>
              <DialogDescription>{t('sala.virtual.noAsistioTexto')}</DialogDescription>
            </DialogHeader>
          )}
          {confirmacion?.tipo === 'noAsistio' && (
            <DialogHeader>
              <DialogTitle>{t('sala.confirmar.noAsistioTitulo', { nombre: confirmacion.fila.nombre })}</DialogTitle>
              <DialogDescription>{t('sala.confirmar.noAsistioTexto')}</DialogDescription>
            </DialogHeader>
          )}
          {confirmacion?.tipo === 'asistio' && (
            <DialogHeader>
              <DialogTitle>{t('sala.porLlegar.confirmarTitulo', { nombre: confirmacion.cita.nombre })}</DialogTitle>
              <DialogDescription>{t('sala.porLlegar.confirmarTexto')}</DialogDescription>
            </DialogHeader>
          )}
          {confirmacion?.tipo === 'deshacer' && (
            <DialogHeader>
              <DialogTitle>{t('sala.confirmar.deshacerTitulo', { nombre: confirmacion.fila.nombre })}</DialogTitle>
              <DialogDescription>{t('sala.confirmar.deshacerTexto')}</DialogDescription>
            </DialogHeader>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmacion(null)}>
              {confirmacion?.tipo === 'asistio' ? t('sala.porLlegar.cancelar') : t('sala.confirmar.volver')}
            </Button>
            <Button onClick={() => void confirmar()}>
              {confirmacion?.tipo === 'asistio'
                ? t('sala.porLlegar.confirmarBoton')
                : confirmacion?.tipo === 'deshacer'
                ? t('sala.confirmar.deshacerBoton')
                : confirmacion?.tipo === 'noAsistio' || confirmacion?.tipo === 'noAsistioVirtual'
                ? t('sala.confirmar.noAsistioBoton')
                : confirmacion?.tipo === 'finalizarVirtual'
                  ? t('sala.confirmar.finalizarIgual')
                  : confirmacion?.fila.estado === 'done'
                  ? t('sala.confirmar.cerrarIgual')
                  : t('sala.confirmar.finalizarIgual')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function TablaFilas({
  titulo,
  filas,
  soloLectura,
  puedeConsultar,
  ocupados,
  recibidoEnMs,
  ahoraMs,
  onAccion,
  virtuales = [],
  ocupadasVirtuales,
  ahoraSrvMs = null,
  onAccionVirtual,
}: {
  titulo: string;
  filas: FilaSala[];
  /** Citas virtuales (van primero: tienen hora de cita exacta y no usan turno). */
  virtuales?: CitaVirtual[];
  ocupadasVirtuales?: ReadonlySet<string>;
  ahoraSrvMs?: number | null;
  onAccionVirtual?: (c: CitaVirtual, a: AccionVirtual) => void;
  soloLectura: boolean;
  puedeConsultar: boolean;
  ocupados: ReadonlySet<number>;
  recibidoEnMs: number;
  ahoraMs: number;
  onAccion: (f: FilaSala, a: AccionFila) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Table aria-label={titulo}>
      <TableHeader>
        <TableRow>
          <TableHead>{t('sala.columnas.turno')}</TableHead>
          <TableHead>{t('sala.columnas.paciente')}</TableHead>
          <TableHead>{t('sala.columnas.cita')}</TableHead>
          <TableHead>{t('sala.columnas.motivo')}</TableHead>
          <TableHead>{t('sala.columnas.espera')}</TableHead>
          <TableHead>{t('sala.columnas.estado')}</TableHead>
          <TableHead>{t('sala.columnas.acciones')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {virtuales.map((c) => (
          <FilaVirtual key={c.citaId} cita={c} ahoraSrvMs={ahoraSrvMs} ocupada={ocupadasVirtuales?.has(c.citaId) === true} onAccion={(cv, a) => onAccionVirtual?.(cv, a)} />
        ))}
        {filas.map((f) => {
          const espera = esperaActualSeg(f.esperaSeg, recibidoEnMs, ahoraMs);
          const acciones = accionesDeFila(f, soloLectura, puedeConsultar);
          const ocupado = ocupados.has(f.id);
          return (
            <TableRow key={f.id} data-estado={f.estado}>
              <TableCell className="font-mono text-xs">{f.turno ?? '—'}</TableCell>
              <TableCell className="text-hce-ink">
                <div className="font-medium">{f.nombre}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {f.modalidad === 'telemedicina' && <EtiquetaVirtual />}
                  {f.cobro === 'sin_cobro' && <Badge variant="clay">{t('sala.marca.sinCobro')}</Badge>}
                  {f.cobro === 'orden_sin_consulta' && <Badge variant="clay">{t('sala.marca.ordenSinConsulta')}</Badge>}
                  {f.estado === 'called' && (
                    <Badge variant="outline">{t('sala.marca.llamado', { n: f.llamados, max: f.maxLlamados })}</Badge>
                  )}
                  {f.deOtroDia && <Badge variant="clay">{t('sala.marca.deOtroDia')}</Badge>}
                  {f.sinEncounter && <Badge variant="clay">{t('sala.marca.sinHistoria')}</Badge>}
                  {f.consultaAbierta && <Badge variant="clay">{t('sala.marca.consultaAbierta')}</Badge>}
                </div>
                {f.alerta && <div className="mt-1 text-xs text-amber-700">{t(`sala.alerta.${f.alerta}`)}</div>}
              </TableCell>
              <TableCell className="font-mono text-xs">{horaEcuadorDe(f.citaHora) || '—'}</TableCell>
              <TableCell className="text-hce-muted">{f.motivo ?? '—'}</TableCell>
              <TableCell className="whitespace-nowrap font-mono text-xs">{espera === null ? '—' : esperaLegible(espera)}</TableCell>
              <TableCell className="whitespace-nowrap text-xs">
                {f.estado === 'done' && f.ordenEnviada
                  ? t('sala.marca.ordenEnviada')
                  : f.estado === 'no_show' && f.estadoMotivo
                    ? t(`sala.estado.${f.estadoMotivo}`)
                    : t(`sala.estado.${f.estado}`)}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1.5">
                  {acciones.map((a) => (
                    <Button
                      key={a}
                      size="sm"
                      variant={a === 'iniciar' || a === 'abrirHistoria' || a === 'reintentarHistoria' ? 'default' : a === 'noAsistio' || a === 'deshacer' ? 'ghost' : 'outline'}
                      disabled={ocupado}
                      aria-label={t('sala.accion.de', { accion: t(`sala.accion.${a}`), nombre: f.nombre })}
                      onClick={() => onAccion(f, a)}
                    >
                      {t(`sala.accion.${a}`)}
                    </Button>
                  ))}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
